import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Plus, FileText, Download, Search, ExternalLink, DollarSign,
  Send, CheckCircle, Clock
} from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-600",
  sent: "bg-orange-100 text-orange-700",
  approved: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-700",
};

export default function EstimatesListPage() {
  const [estimates, setEstimates] = useState([]);
  const [leads, setLeads] = useState({});
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [estRes, leadsRes] = await Promise.all([
          api.get("/estimates"),
          api.get("/leads"),
        ]);
        setEstimates(estRes.data);
        const leadMap = {};
        leadsRes.data.forEach((l) => { leadMap[l.id] = l; });
        setLeads(leadMap);
      } catch (err) { console.error("Failed to load estimates:", err); toast.error("Failed to load estimates"); }
    };
    fetchData();
  }, []);

  const handleDownloadPdf = async (e, estId) => {
    e.stopPropagation();
    try {
      const response = await api.get(`/estimates/${estId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimate_${estId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Failed to download PDF"); }
  };

  const handleMarkSent = async (e, estId) => {
    e.stopPropagation();
    try {
      await api.put(`/estimates/${estId}`, { status: "sent" });
      setEstimates((prev) => prev.map((est) => est.id === estId ? { ...est, status: "sent" } : est));
      toast.success("Marked as sent");
    } catch { toast.error("Failed to update"); }
  };

  const handleApprove = async (e, estId) => {
    e.stopPropagation();
    try {
      await api.put(`/estimates/${estId}`, { status: "approved" });
      setEstimates((prev) => prev.map((est) => est.id === estId ? { ...est, status: "approved" } : est));
      toast.success("Estimate approved!");
    } catch { toast.error("Failed to update"); }
  };

  const handleSendToCustomer = async (e, estId) => {
    e.stopPropagation();
    try {
      const { data } = await api.post("/estimates/send-to-customer", { estimate_id: estId });
      if (data.success) {
        setEstimates((prev) => prev.map((est) => est.id === estId ? { ...est, status: "sent" } : est));
        toast.success("Estimate sent to customer via SMS!");
      } else {
        toast.error(`SMS failed: ${data.sms_result?.error || "Unknown error"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    }
  };

  const getApprovalLink = (estId) => {
    return `${window.location.origin}/approve/${estId}`;
  };

  const copyApprovalLink = (e, estId) => {
    e.stopPropagation();
    navigator.clipboard.writeText(getApprovalLink(estId));
    toast.success("Approval link copied to clipboard!");
  };

  const filtered = estimates.filter((est) => {
    if (!search) return true;
    const lead = leads[est.lead_id];
    const q = search.toLowerCase();
    return (
      lead?.name?.toLowerCase().includes(q) ||
      est.fence_type?.toLowerCase().includes(q) ||
      est.id?.includes(q)
    );
  });

  const totalValue = estimates.reduce((sum, e) => sum + (e.total || 0), 0);
  const sentCount = estimates.filter((e) => e.status === "sent").length;
  const approvedCount = estimates.filter((e) => e.status === "approved").length;

  return (
    <div className="space-y-6" data-testid="estimates-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 tracking-tight">Estimates</h1>
          <p className="text-sm text-zinc-500 mt-1">{estimates.length} total estimates</p>
        </div>
        <Button
          onClick={() => navigate("/estimates/new")}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="create-estimate-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Estimate
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border border-zinc-200 shadow-none">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="bg-green-50 p-2 rounded-lg"><DollarSign className="w-4 h-4 text-green-600" /></div>
            <div>
              <p className="text-xs text-zinc-500">Total Value</p>
              <p className="text-lg font-extrabold text-zinc-900">${totalValue.toLocaleString(undefined, { minimumFractionDigits: 0 })}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200 shadow-none">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="bg-orange-50 p-2 rounded-lg"><Send className="w-4 h-4 text-orange-600" /></div>
            <div>
              <p className="text-xs text-zinc-500">Sent</p>
              <p className="text-lg font-extrabold text-zinc-900">{sentCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200 shadow-none">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="bg-emerald-50 p-2 rounded-lg"><CheckCircle className="w-4 h-4 text-emerald-600" /></div>
            <div>
              <p className="text-xs text-zinc-500">Approved</p>
              <p className="text-lg font-extrabold text-zinc-900">{approvedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search estimates by customer name, fence type..."
          className="pl-10 border-zinc-300"
          data-testid="estimates-search"
        />
      </div>

      {/* Estimates List */}
      {filtered.length === 0 ? (
        <Card className="border border-zinc-200 shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-12 h-12 text-zinc-300 mb-3" />
            <p className="text-zinc-500 text-sm">{search ? "No estimates match your search" : "No estimates yet. Create your first one!"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((est) => {
            const lead = leads[est.lead_id];
            return (
              <Card
                key={est.id}
                className="border border-zinc-200 shadow-none hover:border-orange-300 transition-colors cursor-pointer"
                onClick={() => navigate(`/estimates/${est.id}`)}
                data-testid={`estimate-row-${est.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-zinc-900">{lead?.name || "Unknown Customer"}</p>
                        <Badge className={`text-xs capitalize ${STATUS_COLORS[est.status] || ""}`}>
                          {est.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        {est.fence_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())} - {est.linear_footage}ft x {est.height}ft
                        {est.num_gates > 0 && ` - ${est.num_gates} gate${est.num_gates > 1 ? "s" : ""}`}
                      </p>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        #{est.id?.slice(0, 8).toUpperCase()} - {new Date(est.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-extrabold text-orange-600">${(est.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-8 px-2" onClick={(e) => handleDownloadPdf(e, est.id)} data-testid={`download-est-${est.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {(est.status === "draft" || est.status === "sent") && (
                          <Button size="sm" className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => handleSendToCustomer(e, est.id)} data-testid={`sms-est-${est.id}`} title="Send to customer via SMS">
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {est.status === "sent" && (
                          <Button size="sm" className="h-8 px-2 bg-green-600 hover:bg-green-700 text-white" onClick={(e) => handleApprove(e, est.id)} data-testid={`approve-est-${est.id}`} title="Mark as approved">
                            <CheckCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-8 px-2" onClick={(e) => copyApprovalLink(e, est.id)} data-testid={`copy-link-${est.id}`} title="Copy approval link">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
